/**
 * Elevation Selector DOM Component
 *
 * Renders the exterior elevation chooser used in Lot & Elevation detail panels.
 *
 * @module nativeInteractiveSiteMap/components/elevationSelector.dom
 */

import fastdom from 'fastdom';
import { backButtonIconSvg } from 'constants/svg-assets';
import sortElevations from '../services/elevationSortService';

const DRAG_TRAVEL_CLICK_SUPPRESSION_THRESHOLD = 5;
// An elevation image with no title shows no label at all — no card label and no selected pill
// (NHS20-9080); the pill is hidden via this utility since its own class sets display: block.
const HIDDEN_CLASS = 'nhs-u-display--none';

function renderElevationCard(elevation, { isSelected = false, showSelectionControl = true } = {}) {
  const optionClasses = [
    'nhs-c-elevation-selector__option',
    'nhs-mq-elevation-selector__option-large'
  ];

  if (isSelected) {
    optionClasses.push('nhs-c-elevation-selector__option--selected');
  }

  if (!showSelectionControl) {
    optionClasses.push('nhs-c-elevation-selector__option--single');
  }

  const selectionIndicatorHtml = showSelectionControl
    ? '<span class="nhs-c-elevation-selector__selection-indicator" aria-hidden="true"></span>'
    : '';

  const imageLoadingMode = isSelected || !showSelectionControl ? 'eager' : 'lazy';

  const cardTag = showSelectionControl ? 'button' : 'article';
  // data-elevation-title is the on-screen label (drives the selected pill, blank when untitled);
  // data-elevation-lead-value is what the Lead API and the exterior-selected GA4/Segment events
  // receive — the title, or "Elevation <Display Order>" when it has none (NHS20-9080).
  const cardAttributes = showSelectionControl
    ? `type="button"
      aria-pressed="${isSelected ? 'true' : 'false'}"
      data-elevation-option
      data-elevation-id="${elevation.id}"
      data-elevation-title="${elevation.title}"
      data-elevation-lead-value="${elevation.leadValue}"`
    : '';

  const imageHtml = `
      <img
        class="nhs-c-elevation-selector__image"
        data-siteplan-card-image
        src="${elevation.url}"
        alt=""
        draggable="false"
        loading="${imageLoadingMode}" />
    `;

  const labelHtml = elevation.title
    ? `<span class="nhs-c-elevation-selector__option-badge nhs-o-typography--sans-body-1-small-regular nhs-u-color--black-1000">${elevation.title}</span>`
    : '';

  const descriptionHtml = elevation.description
    ? `<p class="nhs-c-elevation-selector__description nhs-o-typography--sans-label-1-extra-extra-small-regular nhs-u-color--gray-900">${elevation.description}</p>`
    : '';

  return `
    <${cardTag}
      class="${optionClasses.join(' ')}"
      ${cardAttributes}>
      <div class="nhs-c-elevation-selector__image-wrapper nhs-mq-elevation-selector__image-wrapper-large">
        ${imageHtml}
      </div>
      <div class="nhs-u-display--flex nhs-u-width--full nhs-u-items-center nhs-u-justify-content-space-between nhs-u-padding--400 nhs-u-gap--300">
        <div class="nhs-u-display--flex nhs-u-flex-column nhs-u-gap--50 nhs-u-flex--1 nhs-u-min-width--0">
          ${labelHtml}
          ${descriptionHtml}
        </div>
        ${selectionIndicatorHtml}
      </div>
    </${cardTag}>
  `;
}

function renderDisclosureHeader(title) {
  return `
    <summary data-elevation-summary class="nhs-c-elevation-selector__summary nhs-u-display--flex nhs-u-items-center nhs-u-justify-content-space-between nhs-u-gap--300 nhs-u-cursor--pointer nhs-u-margin--bottom-500">
      <h2 class="nhs-c-elevation-selector__title nhs-mq-elevation-selector__title-large nhs-u-color--black-1000">Available elevations</h2>
      <span class="nhs-c-elevation-selector__summary-meta nhs-u-display--flex nhs-u-items-center nhs-u-gap--400">
        <span class="nhs-c-elevation-selector__badge${title ? '' : ` ${HIDDEN_CLASS}`}" data-elevation-selected-badge>
          ${title}
        </span>
        <span class="nhs-c-elevation-selector__chevron" aria-hidden="true">${backButtonIconSvg}</span>
      </span>
    </summary>
  `;
}

function renderSingleElevation(elevation) {
  return `
    <details data-elevation-disclosure class="nhs-c-elevation-selector__disclosure" open>
      ${renderDisclosureHeader(elevation.title)}
      <div class="nhs-c-elevation-selector__list nhs-c-elevation-selector__list--single" data-elevation-list>
        ${renderElevationCard(elevation, { isSelected: true, showSelectionControl: false })}
      </div>
    </details>
  `;
}

function renderMultipleElevations(elevations, selectedId) {
  const cardsHtml = elevations
    .map((elevation) => renderElevationCard(elevation, { isSelected: elevation.id === selectedId }))
    .join('');

  const selectedElevation = elevations.find((elevation) => elevation.id === selectedId) || elevations[0];

  return `
    <details data-elevation-disclosure class="nhs-c-elevation-selector__disclosure" open>
      ${renderDisclosureHeader(selectedElevation.title)}
      <div class="nhs-c-elevation-selector__list nhs-mq-elevation-selector__list-large" data-elevation-list>
        ${cardsHtml}
      </div>
    </details>
  `;
}

/**
 * Creates the elevation selector component.
 * @param {Array} elevations - Elevation collection for the selected plan/unit.
 * @param {Object} [options]
 * @param {(elevation: Object|null) => void} [options.onSelectionChange] - Called with the
 *   currently selected elevation object whenever it changes, including once at construction with
 *   the initial default (NHS20-8974 — lets a sibling component, e.g. the lead form footer, know
 *   which elevation the user has picked without needing this selector's local state).
 * @returns {{ getElement: Function, remove: Function }}
 */
export default function createElevationSelectorDom(elevations = [], { onSelectionChange = () => {} } = {}) {
  const sortedElevations = sortElevations(elevations);
  const wheelListenerOptions = { passive: false };

  const state = {
    dragStartX: 0,
    dragStartScrollLeft: 0,
    dragTravelX: 0,
    isDragging: false,
    root: null,
    listElement: null,
    optionButtons: [],
    selectedBadgeElement: null,
    selectedElevationId: sortedElevations[0]?.id || null,
    suppressOptionClick: false,
  };

  function updateSelectedOption(nextId) {
    // nextId comes from a DOM dataset value (button.dataset.elevationId), which is always a
    // string, while sortedElevations' own `id` is whatever type the API returned (a number here —
    // see Bdx.Nhs.Model.SiteMap.LotPlanImage.Id). Compare/store as strings throughout so this
    // never mismatches against the array's numeric ids (NHS20-9074 — a strict-equality mismatch
    // here made the elevation selection resolve to null on every click).
    if (!nextId || String(nextId) === String(state.selectedElevationId)) {
      return;
    }

    state.selectedElevationId = nextId;

    const selectedButton = state.optionButtons.find((button) => button.dataset.elevationId === nextId);
    const selectedTitle = selectedButton?.dataset.elevationTitle || '';

    fastdom.mutate(() => {
      state.optionButtons.forEach((button) => {
        const isSelected = button.dataset.elevationId === nextId;
        button.classList.toggle('nhs-c-elevation-selector__option--selected', isSelected);
        button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      });

      if (state.selectedBadgeElement) {
        state.selectedBadgeElement.textContent = selectedTitle;
        state.selectedBadgeElement.classList.toggle(HIDDEN_CLASS, !selectedTitle);
      }
    });

    onSelectionChange(sortedElevations.find((elevation) => String(elevation.id) === String(nextId)) ?? null);
  }

  function handleOptionClick(event) {
    if (state.suppressOptionClick) {
      event.preventDefault();
      return;
    }

    const nextId = event.currentTarget?.dataset?.elevationId;
    updateSelectedOption(nextId);
  }

  function handleWheelScroll(event) {
    if (!state.listElement) {
      return;
    }

    const { clientWidth, scrollWidth } = state.listElement;
    if (scrollWidth <= clientWidth + 1) {
      return;
    }

    const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY)
      ? event.deltaX
      : event.deltaY;

    if (!delta) {
      return;
    }

    const maxScrollLeft = Math.max(0, scrollWidth - clientWidth);
    const previousScrollLeft = state.listElement.scrollLeft;
    const nextScrollLeft = Math.min(
      maxScrollLeft,
      Math.max(0, previousScrollLeft + delta)
    );

    if (Math.abs(nextScrollLeft - previousScrollLeft) < 0.5) {
      return;
    }

    state.listElement.scrollLeft = nextScrollLeft;

    if (Math.abs(state.listElement.scrollLeft - previousScrollLeft) >= 0.5) {
      event.preventDefault();
    }
  }

  function continueDragScroll(event) {
    if (!state.listElement || !state.isDragging) {
      return;
    }

    event.preventDefault();

    const deltaX = event.clientX - state.dragStartX;
    state.dragTravelX = Math.abs(deltaX);
    state.listElement.scrollLeft = state.dragStartScrollLeft - deltaX;
  }

  function endDragScroll() {
    if (!state.listElement || !state.isDragging) {
      return;
    }

    state.isDragging = false;
    state.listElement.classList.remove('nhs-c-elevation-selector__list--dragging');
    window.removeEventListener('mousemove', continueDragScroll);
    window.removeEventListener('mouseup', endDragScroll);

    if (state.dragTravelX > DRAG_TRAVEL_CLICK_SUPPRESSION_THRESHOLD) {
      state.suppressOptionClick = true;
      setTimeout(() => {
        state.suppressOptionClick = false;
      }, 0);
    }
  }

  function beginDragScroll(event) {
    if (!state.listElement || event.button !== 0) {
      return;
    }

    if (state.listElement.scrollWidth <= state.listElement.clientWidth + 1) {
      return;
    }

    state.dragStartX = event.clientX;
    state.dragStartScrollLeft = state.listElement.scrollLeft;
    state.dragTravelX = 0;
    state.isDragging = true;
    event.preventDefault();
    state.listElement.classList.add('nhs-c-elevation-selector__list--dragging');
    window.addEventListener('mousemove', continueDragScroll);
    window.addEventListener('mouseup', endDragScroll);
  }

  function initializeDom() {
    if (!sortedElevations.length) {
      return;
    }

    state.root = document.createElement('div');
    state.root.className = 'nhs-c-elevation-selector nhs-u-width--full nhs-u-flex-column nhs-u-gap--300';

    if (sortedElevations.length === 1) {
      state.root.innerHTML = renderSingleElevation(sortedElevations[0]);
      onSelectionChange(sortedElevations[0]);
      return;
    }

    state.root.innerHTML = renderMultipleElevations(sortedElevations, state.selectedElevationId);
    
    state.listElement = state.root.querySelector('[data-elevation-list]');
    state.optionButtons = Array.from(state.root.querySelectorAll('[data-elevation-option]'));
    state.selectedBadgeElement = state.root.querySelector('[data-elevation-selected-badge]');

    state.optionButtons.forEach((button) => {
      button.addEventListener('click', handleOptionClick);
    });

    state.listElement?.addEventListener('wheel', handleWheelScroll, wheelListenerOptions);
    state.listElement?.addEventListener('mousedown', beginDragScroll);
    state.listElement?.addEventListener('dragstart', (event) => event.preventDefault());

    onSelectionChange(sortedElevations.find((elevation) => elevation.id === state.selectedElevationId) ?? null);
  }

  initializeDom();

  return {
    getElement() {
      return state.root;
    },

    remove() {
      state.listElement?.removeEventListener('wheel', handleWheelScroll, wheelListenerOptions);
      state.listElement?.removeEventListener('mousedown', beginDragScroll);
      window.removeEventListener('mouseup', endDragScroll);
      window.removeEventListener('mousemove', continueDragScroll);

      state.optionButtons.forEach((button) => {
        button.removeEventListener('click', handleOptionClick);
      });

      fastdom.mutate(() => {
        state.root?.remove();
        state.root = null;
      });
    },
  };
}
